/** A fighter still standing, as the spectator can follow it. */
export interface StandingFighter {
  id: string;
  name: string;
}

export interface SpectatorView {
  /** Knocked out in the Arena KO match under way: no coming back (D42). */
  out: boolean;
  /** Team match: the place in the ranking is the team's, not shown here. */
  teams: boolean;
  /** Fighters still standing, in join order. */
  standing: readonly StandingFighter[];
}

/**
 * Arena KO, to the last blood (D42): an eliminated player watches the rest of
 * the match or leaves. The card tells the place; watching follows a fighter
 * still standing, the one who scored the KO first, and "Cambia" moves on.
 */
export class SpectatorPanel {
  onLeave: () => void = () => {};

  private readonly card = element("eliminated");
  private readonly placeText = element("eliminated-place");
  private readonly bar = element("spectator-bar");
  private readonly label = element("spectator-label");
  private out = false;
  /** Chose "Guarda la partita". */
  private watching = false;
  private watched: string | undefined;
  /** Ids of the fighters standing, for "Cambia". */
  private standingIds: readonly string[] = [];
  private rendered = "";

  constructor() {
    element("eliminated-watch").addEventListener("click", () => { this.watching = true; });
    element("eliminated-leave").addEventListener("click", () => this.onLeave());
    element("spectator-leave").addEventListener("click", () => this.onLeave());
    element("spectator-next").addEventListener("click", () => { this.watched = this.after(this.watched); });
  }

  /** The KO that eliminated this player: its author is followed first while standing. */
  knockedOutBy(attacker: string): void {
    if (attacker) this.watched = attacker;
  }

  /** Every frame in a room; null outside one. Returns the fighter the camera follows, if any. */
  update(view: SpectatorView | null): string | undefined {
    const out = view?.out ?? false;
    if (out && !this.out && view) {
      // The place is set when falling: everyone still standing finishes ahead.
      this.placeText.textContent = view.teams ? "La tua squadra combatte ancora." : `${view.standing.length + 1}° posto`;
    }
    if (!out) {
      this.watching = false;
      if (this.out) this.watched = undefined;
    }
    this.out = out;
    document.body.classList.toggle("spectating", out);
    const standing = view?.standing ?? [];
    this.standingIds = standing.map((f) => f.id);
    if (out && !standing.some((f) => f.id === this.watched)) this.watched = standing[0]?.id;
    const name = standing.find((f) => f.id === this.watched)?.name ?? "";
    const key = `${out}|${this.watching}|${name}`;
    if (key !== this.rendered) {
      this.rendered = key;
      this.card.hidden = !out || this.watching;
      this.bar.hidden = !out || !this.watching;
      this.label.textContent = name ? `Stai guardando ${name}` : "";
    }
    return out && this.watching ? this.watched : undefined;
  }

  /** The next fighter standing after `id`, round the list. */
  private after(id: string | undefined): string | undefined {
    const ids = this.standingIds;
    if (ids.length === 0) return id;
    return ids[(ids.indexOf(id ?? "") + 1) % ids.length];
  }
}

function element(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`#${id} mancante in index.html`);
  return el;
}
